import type { TableColumn, TablePagination } from '@lifebit-ai/lds'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { getImportRequestsTableColumns } from 'modules/requests/ExportAndAccessRequestsTable/importRequestsTableColumns'
import { getImportRequestDetailsPath } from 'modules/requests/requestDetailPaths'
import { useGetImportRequestsVerification } from 'services/import-requests/hooks/useGetImportRequestsVerification'
import { isImportRequestInFlight } from 'services/import-requests/importRequestUtils'
import type {
  GetImportRequestsVerificationParams,
  GetImportRequestsVerificationResponse,
  ImportRequestVerificationListItem
} from 'services/import-requests/types'

const IMPORT_REQUESTS_REFETCH_INTERVAL_MS = 5000

export interface ImportRequestsTableFiltersState {
  assigneeIds: string[]
  fileName: string
  labelIds: string[]
  requesterIds: string[]
  scanResult: string[]
  status: string[]
  workspaceIds: string[]
}

export const defaultImportRequestsTableFiltersState = (): ImportRequestsTableFiltersState => ({
  assigneeIds: [],
  fileName: '',
  labelIds: [],
  requesterIds: [],
  scanResult: [],
  status: [],
  workspaceIds: []
})

export const buildImportRequestsVerificationParams = (
  tableState: { page: number; pageSize: number },
  filtersState: ImportRequestsTableFiltersState
): GetImportRequestsVerificationParams => ({
  page: tableState.page + 1,
  perPage: tableState.pageSize,
  ...(filtersState.fileName ? { fileName: filtersState.fileName } : {}),
  ...(filtersState.status.length ? { status: filtersState.status.join(',') } : {}),
  ...(filtersState.scanResult.length ? { scanResult: filtersState.scanResult.join(',') } : {}),
  ...(filtersState.workspaceIds.length ? { workspaceIds: filtersState.workspaceIds.join(',') } : {}),
  ...(filtersState.requesterIds.length ? { requesterIds: filtersState.requesterIds.join(',') } : {}),
  ...(filtersState.assigneeIds.length ? { assigneeIds: filtersState.assigneeIds.join(',') } : {}),
  ...(filtersState.labelIds.length ? { labelIds: filtersState.labelIds.join(',') } : {})
})

const hasInFlightImportRequests = (data?: GetImportRequestsVerificationResponse): boolean =>
  data?.data.some(request => isImportRequestInFlight(request)) ?? false

export const useImportRequestsTable = (
  queryParams: GetImportRequestsVerificationParams
): {
  columns: TableColumn<ImportRequestVerificationListItem>[]
  importRequests: ImportRequestVerificationListItem[]
  isLoading: boolean
  openImportRequest: (requestId: string) => void
  pagination: TablePagination
  showPagination: boolean
} => {
  const navigate = useNavigate()

  const { data: paginatedResponse, isLoading } = useGetImportRequestsVerification(queryParams, {
    refetchInterval: query =>
      hasInFlightImportRequests(query.state.data) ? IMPORT_REQUESTS_REFETCH_INTERVAL_MS : false
  })

  const columns = useMemo(() => getImportRequestsTableColumns(), [])

  const importRequests = paginatedResponse?.data ?? []

  const pagination: TablePagination = useMemo(
    () => ({
      pageSize: queryParams.perPage,
      currentPage: queryParams.page - 1,
      totalRows: paginatedResponse?.pagination?.totalCount || 0,
      pageSizeOptions: [8, 10, 20, 50],
      onPageChange: () => undefined,
      onPageSizeChange: () => undefined
    }),
    [paginatedResponse?.pagination?.totalCount, queryParams.page, queryParams.perPage]
  )

  const openImportRequest = useCallback(
    (requestId: string): void => {
      if (!requestId) return

      navigate(getImportRequestDetailsPath(requestId))
    },
    [navigate]
  )

  return {
    columns,
    importRequests,
    isLoading,
    openImportRequest,
    pagination,
    showPagination: importRequests.length > 0
  }
}
